import { h, Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import * as Sentry from "@sentry/browser";

import {
    AddressElement,
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    Elements,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import {
    StripeAddressElementChangeEvent,
    StripeCardNumberElement,
    StripeElementChangeEvent,
    StripeElementType
} from "@stripe/stripe-js";
import { loadStripe } from "retry-stripe-loader";

import { CheckoutLoadingState } from "../../../apps/containers/Pay/CheckoutContainer/CheckoutContainerContext";
import { calculateSubscriptionPrice } from "../../../foundation/api";
import { Billing, CalculateSubscriptionPriceResponse, Coupon, SubscriptionPlan } from "../../../foundation/api/model";
import { BillingAddress } from "../../../foundation/api/model/common/Billing";
import { STRIPE_PUBLISHABLE_KEY } from "../../../foundation/constants";
import { BillingPeriod } from "../../../foundation/enums";
import ClientError from "../../../foundation/errors/clientError";
import { handleError } from "../../../foundation/errors/errors";
import ServerError from "../../../foundation/errors/serverError";
import classname from "../../../foundation/utils/classname";
import { formatCurrency } from "../../../foundation/utils/legacy";
import { keysOf } from "../../../foundation/utils/object";
import { formatPriceForUI, getPeriodText } from "../../../foundation/utils/payment";

import Tooltip from "../../../library/Tooltip";
import Button from "../../../library/v2/Button";

import Alert from "../../v2/Alert";
import CircleLoader from "../../v2/CircleLoader";
import IconCheck from "../../v2/Icons/IconCheck";

import { getCardStyle, getStripeConfig } from "./stripeConfigs";

import styles from "./PaymentInfo.module.css";

const STRIPE_RETRY_COUNT = 8;

const FULL_FORM_DATA = {
    cardNumberReady: false,
    cvcReady: false,
    expirationReady: false,
    addressReady: false,
    taxInfoReady: false
};

const FORM_DATA_OMIT_CARD = {
    cardNumberReady: true,
    cvcReady: true,
    expirationReady: true,
    addressReady: false,
    taxInfoReady: false
};

const US_COUNTRY_CODE = "US";

function isValidUSZip(zipCode: string) {
    return /^\d{5}(-\d{4})?$/.test(zipCode);
}

type FormDataProperties = "cardNumberReady" | "cvcReady" | "expirationReady" | "addressReady" | "taxInfoReady";
type FormDataReadyInfo = {
    [key in FormDataProperties]: boolean;
};

type StripeValidationError = {
    elementType: StripeElementType;
    message: string;
};

const stripePromise = loadStripe(
    STRIPE_PUBLISHABLE_KEY,
    {
        retryOptions: {
            onFailedAttempt: error => {
                if (error.retriesLeft === 0) {
                    Sentry.captureException(
                        new Error(`STRIPE RETRIES FAILED (${STRIPE_RETRY_COUNT} retries), ${error}`)
                    );
                }
            },
            retries: STRIPE_RETRY_COUNT
        }
    }
);

interface Props {
    paymentInProgress?: boolean;
    billingData?: Billing;
    appliedCoupon?: Coupon;
    totalPrice: number;
    billingPeriod: BillingPeriod;
    selectedPlan: SubscriptionPlan;
    taxInfo?: CalculateSubscriptionPriceResponse;
    personalPlanTier?: number;
    reservedSeatCount?: number;
    paymentType: "org" | "personal";
    omitCreditCard?: boolean;
    onPay: (stripeToken: string | undefined, billing: Billing) => Promise<boolean | void>;
    setLoadingState?: (loading: CheckoutLoadingState) => void;
    setTaxInfo: (taxInfo: CalculateSubscriptionPriceResponse | undefined) => void;
}

function deleteEmptyAddressFields(address: BillingAddress) {
    keysOf<BillingAddress>(address).forEach(key => {
        if (!address[key]) {
            delete address[key];
        }
    });
}

function PaymentInfoInner(props: Props) {
    const stripe = useStripe();
    const elements = useElements();
    const [focusedElement, setFocusedElement] = useState<StripeElementType | null>(null);
    const [cardValidationErrors, setCardValidationErrors] = useState<StripeValidationError[]>([]);
    const [addressData, setAddressData] = useState<BillingAddress | null>(null);
    const [zipCodeValidationError, setZipCodeValidationError] = useState<string | null>(null);
    const [name, setName] = useState<string>("");
    const [isPayLoading, setIsPayLoading] = useState(false);
    const [showTopBorder, setShowTopBorder] = useState(false);
    const [showBottomBorder, setShowBottomBorder] = useState(false);
    const [stripeCardErrorVisible, setStripeCardErrorVisible] = useState<boolean>(false);

    const {
        paymentInProgress,
        totalPrice,
        taxInfo,
        selectedPlan,
        billingPeriod,
        appliedCoupon,
        personalPlanTier,
        reservedSeatCount,
        paymentType,
        billingData,
        omitCreditCard,
        setTaxInfo,
        onPay,
        setLoadingState
    } = props;

    const initialFormData = omitCreditCard ? FORM_DATA_OMIT_CARD : FULL_FORM_DATA;
    const [formDataReady, setFormDataReady] = useState<FormDataReadyInfo>(initialFormData);

    const finalPrice = taxInfo ? formatPriceForUI(taxInfo.total) : totalPrice;
    const cardStyle = getCardStyle();

    useEffect(() => {
        if (formDataReady.addressReady) {
            if (addressData?.country === US_COUNTRY_CODE) {
                if (addressData.state && isValidUSZip(addressData.postalCode)) {
                    getStateTaxInfo(addressData);
                }
            } else {
                setFormDatumReady("taxInfoReady", true);
            }
        }
    }, [billingPeriod, appliedCoupon]);

    function createCardFormChangeHandler(datumProperty: FormDataProperties) {
        return (event: StripeElementChangeEvent) => {
            setFormDatumReady(datumProperty, event.complete);

            const { elementType, error, complete } = event;
            if (error) {
                setCardValidationErrors(prevState => {
                    const filteredState = prevState
                        .filter(cardValidationError => cardValidationError.elementType !== elementType);
                    return [...filteredState, { elementType, message: error.message }];
                });
            }

            if (complete) {
                setCardValidationErrors(prevState => (
                    prevState.filter(cardValidationError => cardValidationError.elementType !== elementType)
                ));
            }
        };
    }

    function getValidationMessage() {
        const cardDataReady = formDataReady["cardNumberReady"] &&
            formDataReady["expirationReady"] &&
            formDataReady["cvcReady"];

        if (zipCodeValidationError) {
            return zipCodeValidationError;
        }

        if (cardValidationErrors.length) {
            return cardValidationErrors[cardValidationErrors.length - 1].message;
        }

        if (!cardDataReady) {
            return "The credit card information doesn’t look right. Please check and try again.";
        }

        if (!addressData) {
            return "Please provide your address details.";
        }

        if (!formDataReady.addressReady) {
            return "The address doesn’t look right. Please check and try again.";
        }

        if (!formDataReady.taxInfoReady) {
            return "The ZIP Code is not valid for the selected state.";
        }
    }

    function setFormDatumReady(datumProperty: FormDataProperties, isReady: boolean) {
        setFormDataReady(prevDataReady => ({
            ...prevDataReady,
            [datumProperty]: isReady
        }));
    }

    function handleScroll(event: Event) {
        const element = event.currentTarget as HTMLDivElement;
        let showTopBorderComputed = false;
        let showBottomBorderComputed = false;

        // Add top border if there's content above the visible area
        if (element.scrollTop > 0) {
            showTopBorderComputed = true;
        }

        // Add bottom border if there's content below the visible area
        if (element.scrollHeight > element.clientHeight + element.scrollTop) {
            showBottomBorderComputed = true;
        }

        setShowBottomBorder(showBottomBorderComputed);
        setShowTopBorder(showTopBorderComputed);
    }

    function submitButtonDisabled() {
        return !Object.values(formDataReady).reduce((acc, cur) => acc && cur, true);
    }

    function renderCardMessage() {
        if (cardValidationErrors.length) {
            // Get the last validation error
            const { message } = cardValidationErrors[cardValidationErrors.length - 1];
            return (
                <div class={classname(styles.cardMessage, styles.error)}>
                    <span>{message}</span>
                </div>
            );
        }

        const cardDataReady = formDataReady["cardNumberReady"] &&
            formDataReady["expirationReady"] &&
            formDataReady["cvcReady"];

        if (cardDataReady) {
            return (
                <div class={styles.cardMessage}>
                    <IconCheck color="var(--marker-jade)"/>
                    <span>Card details look good.</span>
                </div>
            );
        }

        return (
            <div class={classname(styles.cardMessage, styles.empty)}/>
        );
    }

    function handleTryAgain() {
        setStripeCardErrorVisible(false);
    }

    function renderStripeCardAlert() {
        if (!stripeCardErrorVisible) {
            return null;
        }

        return (
            <Alert title="Processing payment failed"
                description={(
                    <>
                        We had an issue processing the payment.
                        Please try again with another card and contact us at&nbsp;
                        <a href="mailto:support@zeplin.io?subject=Payment%20Error"
                            target="_blank"
                            rel="noopener noreferrer">
                            support@zeplin.io
                        </a> if the issue persists.
                    </>
                )}
                isDismissible={true}
                primaryButtonProps={{
                    children: "Try again",
                    onClick: handleTryAgain
                }}/>
        );
    }

    async function getToken() {
        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        const token = await stripe.createToken(elements.getElement("cardNumber")!);
        if (token.error || !token.token) {
            setStripeCardErrorVisible(true);
            return;
        }

        return token.token.id;
    }

    async function getStateTaxInfo(address: BillingAddress) {
        deleteEmptyAddressFields(address);
        setFormDatumReady("taxInfoReady", false);
        setLoadingState?.("Tax");
        const priceParameters = {
            plan: selectedPlan.id,
            tierQuantity: personalPlanTier,
            address,
            coupon: appliedCoupon?.id,
            seatCount: reservedSeatCount
        };

        try {
            const taxResponse = await calculateSubscriptionPrice(priceParameters);
            setTaxInfo(taxResponse);

            const taxInfoReady = taxResponse.taxes ? taxResponse.taxes.every(tax => tax.state === address.state) : true;
            setFormDatumReady("taxInfoReady", taxInfoReady);
            setZipCodeValidationError(null);
        } catch (error) {
            setZipCodeValidationError((error as (ClientError | ServerError)).message);
            handleError(error);
        } finally {
            setLoadingState?.(null);
        }
    }

    function onAddressChange(event: StripeAddressElementChangeEvent) {
        setFormDatumReady("addressReady", event.complete);
        const {
            name: newName,
            address: {
                city,
                country,
                line1,
                line2,
                postal_code: postalCode,
                state
            }
        } = event.value;
        const newAddress: BillingAddress = {
            city,
            country,
            line1,
            line2,
            postalCode,
            state
        };

        if (country === US_COUNTRY_CODE) {
            const stateChanged = addressData?.state !== state;
            const postCodeChanged = addressData?.postalCode !== postalCode;

            if (state && isValidUSZip(postalCode) && (stateChanged || postCodeChanged)) {
                getStateTaxInfo(newAddress);
            }
        } else {
            setFormDatumReady("taxInfoReady", true);
        }

        setName(newName);
        setAddressData(newAddress);
    }

    function getDefaultAddressValues() {
        if (!billingData?.address) {
            return { name: billingData?.name ?? name };
        }

        /* eslint-disable camelcase */
        const { postalCode: postal_code, ...rest } = billingData.address;

        return {
            name: billingData.name,
            address: { postal_code, ...rest }
        };
        /* eslint-enable camelcase */
    }

    async function handlePay() {
        let redirected: boolean | void = false;

        try {
            setIsPayLoading(true);

            const token = !omitCreditCard ? await getToken() : undefined;

            const address = { ...addressData } as BillingAddress;
            deleteEmptyAddressFields(address);
            redirected = await onPay(token, {
                name,
                address: address!
            });
        } finally {
            setIsPayLoading(!!redirected);
        }
    }

    function onCardNumberReady(element: StripeCardNumberElement) {
        element.focus();
    }

    function onStripeElementFocus({ elementType }: { elementType: StripeElementType; }) {
        setFocusedElement(elementType);
    }

    function onStripeElementBlur() {
        setFocusedElement(null);
    }

    function renderSubmitButton() {
        const disabled = submitButtonDisabled() || isPayLoading;
        const submitButton = (
            <Button
                class={classname({ [styles.payButton]: !disabled })}
                disabled={disabled}
                onClick={handlePay}>
                {`Pay ${formatCurrency(finalPrice)} ${getPeriodText(billingPeriod)}`}
            </Button>
        );

        if (disabled) {
            const message = getValidationMessage();
            return (
                <Tooltip targetClass={styles.payButton} content={message}>
                    {submitButton}
                </Tooltip>
            );
        }

        return submitButton;
    }

    return (
        <div class={classname(styles.paymentInfo, {
            [styles.orgPayment]: paymentType === "org",
            [styles.personalPayment]: paymentType === "personal"
        })}>
            <h1 class="heading-3 medium">Enter your payment details</h1>
            <div class={classname(styles.headerCaption, "caption-1")}>
                Provide your {omitCreditCard ? "" : "card details and "}billing address to wrap things up.
            </div>
            <div class={classname(styles.paymentForm, {
                [styles.scrollable]: paymentType === "org",
                [styles.borderTop]: showTopBorder,
                [styles.borderBottom]: showBottomBorder
            })}
            onScroll={handleScroll}>
                {!omitCreditCard && (
                    <div class={styles.paymentRow}>
                        <h2>Card details</h2>
                        <div class={styles.cardDetails}>
                            <div class={styles.label}>Card number</div>
                            <CardNumberElement
                                className={classname(styles.cardInput, { [styles.active]: focusedElement === "cardNumber" })}
                                options={{
                                    style: cardStyle
                                }}
                                onFocus={onStripeElementFocus}
                                onBlur={onStripeElementBlur}
                                onChange={createCardFormChangeHandler("cardNumberReady")}
                                onReady={onCardNumberReady}/>
                            <div class={styles.dateAndCvc}>
                                <div class={styles.date}>
                                    <div class={styles.label}>Expiration date</div>
                                    <CardExpiryElement
                                        className={classname(styles.cardInput, { [styles.active]: focusedElement === "cardExpiry" })}
                                        options={{
                                            style: cardStyle
                                        }}
                                        onFocus={onStripeElementFocus}
                                        onBlur={onStripeElementBlur}
                                        onChange={createCardFormChangeHandler("expirationReady")}/>
                                </div>
                                <div class={styles.cvc}>
                                    <div class={styles.label}>Security number</div>
                                    <CardCvcElement
                                        className={classname(styles.cardInput, { [styles.active]: focusedElement === "cardCvc" })}
                                        options={{
                                            style: cardStyle
                                        }}
                                        onFocus={onStripeElementFocus}
                                        onBlur={onStripeElementBlur}
                                        onChange={createCardFormChangeHandler("cvcReady")}/>
                                </div>
                            </div>
                            {renderCardMessage()}
                        </div>
                    </div>
                )}
                <div class={classname(styles.paymentRow, styles.addressInfoContainer)}>
                    <h2>Billing address</h2>
                    <div class={styles.addressInfo}>
                        <AddressElement
                            options={{
                                mode: "billing",
                                display: {
                                    name: "organization"
                                },
                                defaultValues: getDefaultAddressValues()
                            }}
                            onChange={onAddressChange}/>
                    </div>
                </div>
            </div>
            {renderSubmitButton()}
            {renderStripeCardAlert()}
            {(isPayLoading || paymentInProgress) && (
                <>
                    <div class={styles.loaderOverlay}/>
                    <div className={classname(styles.loader, "body-1")}>
                        <CircleLoader/>
                        Processing payment, this can take a few seconds…
                    </div>
                </>

            )}
        </div>
    );
}

export default function PaymentInfo(props: Props) {
    return (
        <Elements stripe={stripePromise} options={getStripeConfig()}>
            <PaymentInfoInner {...props}/>
        </Elements>
    );
}
